// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { alpha, Box, Chip, Divider, Stack, Typography } from '@mui/material';

// components
import { getTypeColor } from '@/utils/nodeTypeMapper';

// relative
import CollapsibleAlert from './CollapsibleAlert';

const InitiativeInclusionNotice = ({
  virtualInitiativeInclusions,
  mode = 'add',
}) => {
  const { t } = useTranslation('energyxplorer');
  if (!virtualInitiativeInclusions || virtualInitiativeInclusions.length === 0)
    return null;

  const actionWord = t(`nodes.initiative_inclusion.mode_action_word.${mode}`);
  const verb = t(`nodes.initiative_inclusion.mode_verb.${mode}`);
  const count = virtualInitiativeInclusions.length;

  const summary = t('nodes.initiative_inclusion.summary', { count });

  return (
    <CollapsibleAlert severity="warning" summary={summary}>
      <Typography variant="body2" gutterBottom>
        {t('nodes.initiative_inclusion.message_part1')}
        <strong>
          {' '}
          {t('nodes.initiative_inclusion.virtual_collection_node')}
        </strong>
        {t('nodes.initiative_inclusion.message_part2')} <strong>{verb}</strong>{' '}
        {t('nodes.initiative_inclusion.message_part3')} {actionWord}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="caption"
          sx={{
            textTransform: 'uppercase',
          }}
        >
          {t('nodes.initiative_inclusion.affected_initiatives')}
        </Typography>

        <Stack
          spacing={0.25}
          sx={{
            mt: 0.5,
            maxHeight: 200,
            overflowY: 'auto',
          }}
        >
          {virtualInitiativeInclusions.map(({ initiative, viaNode }) => {
            const initiativeLabel =
              initiative.name ||
              initiative.title ||
              `${t('nodes.initiative_list.initiative_prefix')}${
                initiative.energyInitiativeId || initiative.id || ''
              }`.trim();

            const viaLabel =
              viaNode?.name ||
              `${t('nodes.initiative_list.node_prefix')}${viaNode?.energyTreeNodeId || viaNode?.EnergyTreeNodeId}`;

            const viaColor = getTypeColor(viaNode?.type) || 'grey';

            return (
              <Stack
                key={
                  initiative.energyInitiativeId ||
                  initiative.id ||
                  initiative.guid
                }
                direction="row"
                alignItems="center"
                spacing={1}
                sx={(theme) => ({
                  px: 0.75,
                  py: 0.5,
                  borderRadius: 1,
                  transition: 'background-color 120ms ease',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.warning.main, 0.04),
                  },
                })}
              >
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: 'text.primary',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {initiativeLabel}
                  </Typography>
                </Box>
                {t('nodes.initiative_inclusion.via')}
                <Chip
                  label={viaLabel}
                  size="small"
                  variant="filled"
                  sx={{
                    flexShrink: 0,
                    borderRadius: 1,
                    borderColor: viaColor,
                    color: '#fff',
                    bgcolor: viaColor,
                  }}
                />
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </CollapsibleAlert>
  );
};

export default InitiativeInclusionNotice;
